import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import {appendContext} from 'io-ts';
import {reporter} from 'io-ts-reporters';
import {DateTime} from 'luxon';

import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../../srcCommon/services/logger/logger';

export {makeEnumRuntimeType} from '../../../libs/shared/helpers/src/typescript/enumHelpers';

/*
 * Common types.
 */

export enum FrameTypesEnum {
  CALENDAR = 'CALENDAR',
  CUSTOMER_VIEW = 'CUSTOMER_VIEW',
  ENTRY_POINT = 'ENTRY_POINT',
  INTEGRATION = 'INTEGRATION',
  KEYBOARD_HELP = 'KEYBOARD_HELP',
  MANAGER = 'MANAGER',
  PLUGIN = 'PLUGIN',
  START_GUIDE = 'START_GUIDE',
  MULTI_VIEW = 'MULTI_VIEW',
  APPLICATION_ENTRY_POINT = 'APPLICATION_ENTRY_POINT',
}

/*
 * Interfaces.
 */

export type RuntimeType = {readonly _A: object};
export type RuntimeIdType = {readonly _A: {id: number} & object};
export type RuntimeResourceType = {readonly _A: {updatedAt: number} & object};
export type ModelType<T extends RuntimeType> = T['_A'];

export interface ApiType<T extends RuntimeType> {
  name: string;
  runtimeType: T;
  parse(src: any): ModelType<T>;
}

export interface ApiSerializableType<T extends RuntimeType> extends ApiType<T> {
  serialize(src: ModelType<T>): any;
}

/*
 * Constants.
 */

const runtimeTypeLogger = logger.getLogger('runtime-type');

/*
 * ApiType builders.
 */

export function makeApiType<T extends RuntimeType>(
  name: string,
  runtimeType: T,
  parse: (src: any) => ModelType<T>,
): ApiType<T> {
  return {
    name,
    runtimeType,
    parse,
  };
}

export function makeApiSerializableType<T extends RuntimeType>(
  name: string,
  runtimeType: T,
  parse: (src: any) => ModelType<T>,
  serialize: (src: ModelType<T>) => any,
): ApiSerializableType<T> {
  return {
    name,
    runtimeType,
    parse,
    serialize,
  };
}

/*
 * Custom runtime type builders.
 */

export class RefType<A, O = A, I = unknown> extends t.Type<A, O, I> {
  constructor(baseType: t.Type<A, O, I>, targetType: t.Mixed) {
    super('frontref', baseType.is, baseType.validate, baseType.encode);
    this.targetTypeValue = targetType;
  }

  private targetTypeValue: t.Mixed;

  get targetType() {
    return this.targetTypeValue;
  }

  setTargetType(targetType: t.Mixed) {
    this.targetTypeValue = targetType;
  }
}

export function makeIdRefType(targetType: t.Mixed) {
  return new RefType(t.Integer, targetType);
}

export function makeUidRefType(targetType: t.Mixed) {
  return new RefType(t.string, targetType);
}

export const RConversationIdRef = makeIdRefType(t.Integer);
export const RMessageUidRef = makeUidRefType(t.string);
export const RCommentUidRef = makeUidRefType(t.string);
export const RBannerStateUidRef = makeUidRefType(t.string);
export const RInboxIdRef = makeIdRefType(t.Integer);
export const RChannelIdRef = makeIdRefType(t.Integer);
export const RExternalChannelTypeIdRef = makeIdRefType(t.Integer);
export const RLazyTeammateIdRef = makeIdRefType(t.Integer);
export const RCalendarEventVersionUidRef = makeUidRefType(t.string);
export const RCalendarEventUidRef = makeUidRefType(t.string);
export const RContactUidRef = makeUidRefType(t.string);
export const RCardIdRef = makeIdRefType(t.Integer);
export const RCardGroupIdRef = makeIdRefType(t.Integer);
export const RCardNoteIdRef = makeIdRefType(t.Integer);
export const RTagIdRef = makeIdRefType(t.Integer);
export const RRuleIdRef = makeIdRefType(t.Integer);
export const RAccountIdRef = makeIdRefType(t.Integer);
export const RTopicIdRef = makeIdRefType(t.Integer);
export const RLocalApplicationIdRef = makeIdRefType(t.Integer);
export const RApplicationIdRef = makeIdRefType(t.Integer);
export const RLocalApplicationEntryPointIdRef = makeIdRefType(t.Integer);
export const RApiTokenIdRef = makeIdRefType(t.Integer);

export interface DefaultValueFallbackErrorData {
  keyPath: string[];
  expectedType: string;
  actualValue: string;
}

export class DefaultValueFallbackError extends ErrorWithData<DefaultValueFallbackErrorData> {
  constructor(data: DefaultValueFallbackErrorData) {
    const normalizedPath =
      data.keyPath.length > 0
        ? data.keyPath
            .map((key) => {
              if (Number.isInteger(Number(key))) {
                return '*';
              }

              return key;
            })
            .join('.')
        : undefined;

    const message = normalizedPath
      ? `[io-ts-default] Fallback used at ${normalizedPath}`
      : `[io-ts-default] Fallback used`;
    super(message, data);

    this.normalizedPath = normalizedPath;
  }

  private readonly normalizedPath: string | undefined;

  get groupingHash() {
    return this.normalizedPath ? `io-ts-default-${this.normalizedPath}` : 'io-ts-default';
  }
}

/** Build a custom runtime type with a default value in case of parsing failure. */
export function makeDefaultValueType<A, O = A, I = unknown>(type: t.Type<A, O, I>, defaultValue: A) {
  return new t.Type<A, O, I>(
    `Default${type.name}`,
    (value: unknown): value is A => type.is(value),
    (value, context) => {
      const result = type.validate(value, context);

      // If we succeeded, return the result.
      if (E.isRight(result)) {
        return result;
      }

      const fallbackMode = findFallbackMode(context);
      switch (fallbackMode) {
        case FallbackModesEnum.NO_FALLBACK:
          return result;

        case FallbackModesEnum.SILENT_FALLBACK:
          return t.success(defaultValue);

        case FallbackModesEnum.REPORT_FALLBACK:
        default: {
          const errors = result.left;
          errors.forEach((error) => {
            reportToBugsnag(
              new DefaultValueFallbackError({
                keyPath: error.context.slice(1).map((entry) => entry.key),
                expectedType: type.name,
                actualValue:
                  error.value === undefined
                    ? 'undefined'
                    : JSON.stringify(error.value, null, 2).slice(0, 10000),
              }),
            );
          });

          return t.success(defaultValue);
        }
      }
    },
    (value) => type.encode(value),
  );
}

export enum FallbackModesEnum {
  NO_FALLBACK = 'NO_FALLBACK',
  SILENT_FALLBACK = 'SILENT_FALLBACK',
  REPORT_FALLBACK = 'REPORT_FALLBACK',
}

class FallbackModeType<A, O = A, I = unknown> extends t.Type<A, O, I> {
  constructor(type: t.Type<A, O, I>, fallbackMode: FallbackModesEnum) {
    super(`FallbackMode${type.name}`, type.is, type.validate, type.encode);

    this.fallbackMode = fallbackMode;
  }

  public readonly fallbackMode: FallbackModesEnum;
}

export function makeFallbackModeType<A, O = A, I = unknown>(
  type: t.Type<A, O, I>,
  fallbackMode: FallbackModesEnum,
) {
  return new FallbackModeType(type, fallbackMode);
}

function isFallbackModeType<I, A>(type: t.Decoder<I, A>): type is FallbackModeType<A, A, I> {
  return type instanceof FallbackModeType;
}

function findFallbackMode(
  context: t.Context,
  defaultFallbackMode = FallbackModesEnum.REPORT_FALLBACK,
): FallbackModesEnum {
  const fallbackModeType = context.map((entry) => entry.type).find(isFallbackModeType);
  return fallbackModeType ? fallbackModeType.fallbackMode : defaultFallbackMode;
}

// A CODEC for DateTime <> ISO-8601 string.
export const RDateTimeString = new t.Type<DateTime, string, unknown>(
  'DateTimeString',
  (u): u is DateTime => u instanceof DateTime,
  (u, c) =>
    E.either.chain(t.string.validate(u, c), (s: string) => {
      const date = DateTime.fromISO(s, {zone: 'utc'});
      return date.isValid ? t.success(date) : t.failure(u, c);
    }),
  (a) => a.toISO(),
);

export function makeReadonlyArrayIgnoringInvalidValuesType<C extends t.Mixed>(valueCodec: C, name?: string) {
  const baseType = t.readonlyArray(valueCodec, name);

  return new t.ReadonlyArrayType(
    baseType.name,
    baseType.is,
    (unknownInput, context) =>
      E.either.chain(t.UnknownArray.validate(unknownInput, context), (unknownValues) => {
        const validatedValues: t.TypeOf<C>[] = [];

        unknownValues.forEach((unknownValue, index) => {
          const result = valueCodec.validate(
            unknownValue,
            appendContext(context, String(index), valueCodec, unknownValue),
          );

          if (E.isLeft(result)) {
            reporter(result).forEach((error) => {
              runtimeTypeLogger.warn(error, {
                type_name: baseType.name,
                value_type_name: valueCodec.name,
              });
            });
            return;
          }

          validatedValues.push(result.right);
        });

        return t.success(Object.freeze(validatedValues));
      }),
    baseType.decode,
    valueCodec,
  );
}
